"use client";

import { useEffect, useRef, useState } from "react";
export type Phase = "running" | "completing" | "complete" | "done";
export type UseProcessingProgressOptions = {
  stepCount: number;
  isComplete: boolean;
  minProcessingMs?: number;
  onAllStepsComplete?: () => void;
  onContinue?: (() => void) | (() => Promise<void>);
  estimatedDurationMs?: number;
  completionStepDelayMs?: number;
  completionExitDelayMs?: number;
  initialProgress?: number;
};
export type UseProcessingProgressResult = {
  progress: number;
  activeStepIndex: number;
  phase: Phase;
  isProcessing: boolean;
  handleExitComplete: () => void;
};
const CEILING = 87;
const RATE = 0.0012;
const ESTIMATED_DURATION_MS = 20_000;
const DOT_CASCADE_DELAY_MS = 1000;
const COMPLETE_TRANSITION_DELAY_MS = 500;
const FIRST_STEP_DELAY_FACTOR = 0.5;
export function useProcessingProgress({
  stepCount,
  isComplete,
  minProcessingMs,
  onAllStepsComplete,
  estimatedDurationMs = ESTIMATED_DURATION_MS,
  completionStepDelayMs = DOT_CASCADE_DELAY_MS,
  completionExitDelayMs = COMPLETE_TRANSITION_DELAY_MS,
  initialProgress = 0
}: UseProcessingProgressOptions): UseProcessingProgressResult {
  const [progress, setProgress] = useState(initialProgress);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("running");
  const progressRef = useRef(initialProgress);
  const phaseRef = useRef<Phase>("running");
  const activeStepRef = useRef(0);
  const onAllStepsCompleteRef = useRef(onAllStepsComplete);
  const processingStartTimeRef = useRef(Date.now());
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    onAllStepsCompleteRef.current = onAllStepsComplete;
  }, [onAllStepsComplete]);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    activeStepRef.current = activeStepIndex;
  }, [activeStepIndex]);
  useEffect(() => {
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (phase === "complete" || phase === "done") return;
    let raf: number;
    let isDisposed = false;
    const ceiling = phase === "completing" ? 100 : CEILING;
    const rate = phase === "completing" ? RATE * 3 : RATE;
    const tick = () => {
      if (isDisposed) return;
      const p = phaseRef.current;
      if (p === "complete" || p === "done") return;
      const current = progressRef.current;
      const next = Math.max(current, current + (ceiling - current) * rate);
      progressRef.current = next;
      setProgress(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      isDisposed = true;
      cancelAnimationFrame(raf);
    };
  }, [phase]);
  useEffect(() => {
    if (phase !== "running") return;
    if (stepCount <= 1) return;
    const dotInterval = estimatedDurationMs / stepCount;
    const firstStepDelay = dotInterval * FIRST_STEP_DELAY_FACTOR;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    for (let stepIndex = 1; stepIndex < stepCount; stepIndex++) {
      const delay = stepIndex === 1 ? firstStepDelay : firstStepDelay + (stepIndex - 1) * dotInterval;
      timeoutIds.push(setTimeout(() => {
        setActiveStepIndex(stepIndex);
      }, delay));
    }
    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [estimatedDurationMs, phase, stepCount]);
  useEffect(() => {
    if (!isComplete || phase !== "running") return;
    const minimumDuration = Math.max(0, minProcessingMs ?? 0);
    const elapsedMs = Date.now() - processingStartTimeRef.current;
    const remainingMs = minimumDuration - elapsedMs;
    if (remainingMs <= 0) {
      setPhase("completing");
      return;
    }
    completionTimeoutRef.current = setTimeout(() => {
      completionTimeoutRef.current = null;
      setPhase(currentPhase => currentPhase === "running" ? "completing" : currentPhase);
    }, remainingMs);
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
    };
  }, [isComplete, minProcessingMs, phase]);
  useEffect(() => {
    if (phase !== "completing") return;
    const timeoutIds_0: ReturnType<typeof setTimeout>[] = [];
    const currentStep = activeStepRef.current;
    const remainingSteps = stepCount - 1 - currentStep;
    for (let i = 0; i <= remainingSteps; i++) {
      timeoutIds_0.push(setTimeout(() => {
        setActiveStepIndex(currentStep + i);
      }, i * completionStepDelayMs));
    }
    const stepsFinishMs = (remainingSteps + 1) * completionStepDelayMs;
    timeoutIds_0.push(setTimeout(() => {
      progressRef.current = 100;
      setProgress(100);
      // Mark all steps complete once progress reaches 100%.
      setActiveStepIndex(stepCount);
    }, stepsFinishMs));
    const totalMs = stepsFinishMs + completionExitDelayMs;
    timeoutIds_0.push(setTimeout(() => {
      if (onAllStepsCompleteRef.current) {
        setPhase("done");
      } else {
        setPhase("complete");
      }
    }, totalMs));
    return () => {
      timeoutIds_0.forEach(id_0 => clearTimeout(id_0));
    };
  }, [completionExitDelayMs, completionStepDelayMs, phase, stepCount]);
  const handleExitComplete = () => {
    onAllStepsCompleteRef.current?.();
  };
  const isProcessing = phase === "running" || phase === "completing";
  return {
    progress,
    activeStepIndex,
    phase,
    isProcessing,
    handleExitComplete
  };
}